import React from 'react';
import PropTypes from 'prop-types';
import Image from 'components/image';
import Video from 'components/video';
import RichText from 'components/rich-text';
import {
  getResponsiveVideoPreviewImageSources,
  getVideoSourceData,
} from 'js/video-source-utils';
import { imageSourceBreakpoints } from 'js/breakpoints';
import cn from 'classnames';

// eslint-disable-next-line view-models/no-errors
export const MEDIA_POSITIONS = {
  left: 'left',
  right: 'right',
};

const buildVideoPreviewMobilePortraitSources = imageSrc => {
  return [
    {
      imageSrc,
      width: 390,
      height: 694,
      media: imageSourceBreakpoints.xs,
    },
    {
      imageSrc,
      width: 400,
      height: 712,
      media: imageSourceBreakpoints.s,
    },
  ];
};

const buildVideoPreviewDesktopPortraitSources = () => {
  return [];
};

const buildVideoPreviewMobileLandscapeSources = imageSrc => {
  return [
    {
      imageSrc,
      width: 390,
      height: 220,
      media: imageSourceBreakpoints.xs,
    },
    {
      imageSrc,
      width: 728,
      height: 410,
      media: imageSourceBreakpoints.s,
    },
  ];
};

const buildVideoPreviewDesktopLandscapeSources = (imageSrc, mediaPosition) => {
  const isLeftMediaPosition = mediaPosition === MEDIA_POSITIONS.left;
  return [
    {
      imageSrc,
      width: isLeftMediaPosition ? 512 : 420,
      height: isLeftMediaPosition ? 288 : 236,
      media: imageSourceBreakpoints.m,
    },
    {
      imageSrc,
      width: isLeftMediaPosition ? 840 : 695,
      height: isLeftMediaPosition ? 473 : 391,
      media: imageSourceBreakpoints.l,
    },
  ];
};

const buildImageSources = mediaPosition => {
  return [
    {
      width: 390,
      media: imageSourceBreakpoints.xs,
    },
    {
      width: 728,
      media: imageSourceBreakpoints.s,
    },
    {
      width: 512,
      media: imageSourceBreakpoints.m,
    },
    {
      width: mediaPosition === MEDIA_POSITIONS.left ? 840 : 695,
      media: imageSourceBreakpoints.l,
    },
  ];
};

const TextWithImageVideoBlock = ({
  heading,
  textHtml,
  image,
  video,
  mediaPosition,
}) => {
  const { orientation } = getVideoSourceData(
    video?.landscapeSource,
    video?.portraitSource
  );

  return (
    <div
      className={cn('text-with-image-video-block', {
        [`text-with-image-video-block_media-position_${mediaPosition}`]:
          mediaPosition,
      })}
    >
      <div className="text-with-image-video-block__media-container">
        {video ? (
          <Video
            {...video}
            className="text-with-image-video-block__video"
            imageSettings={{
              className: `image_size_text-with-image-video-block-video-preview-${orientation}`,
              sources: getResponsiveVideoPreviewImageSources({
                landscapeSource: video.landscapeSource,
                portraitSource: video.portraitSource,
                buildVideoPreviewMobilePortraitSources,
                buildVideoPreviewDesktopPortraitSources,
                buildVideoPreviewMobileLandscapeSources,
                buildVideoPreviewDesktopLandscapeSources: imageSrc =>
                  buildVideoPreviewDesktopLandscapeSources(
                    imageSrc,
                    mediaPosition
                  ),
              }),
              loading: 'lazy',
            }}
          />
        ) : (
          image && (
            <Image
              {...image}
              sources={buildImageSources(mediaPosition)}
              transform="fit"
              loading="lazy"
              className="text-with-image-video-block__image image_size_text-with-image-video-block"
            />
          )
        )}
      </div>
      <div className="text-with-image-video-block__text-container">
        <h2 className="text-with-image-video-block__heading">{heading}</h2>
        <RichText text={textHtml} />
      </div>
    </div>
  );
};

TextWithImageVideoBlock.propTypes = {
  heading: PropTypes.string,
  textHtml: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  video: PropTypes.exact(Video.propTypes),
  mediaPosition: PropTypes.oneOf(Object.values(MEDIA_POSITIONS)).isRequired,
};

// eslint-disable-next-line view-models/no-errors
export default TextWithImageVideoBlock;
